import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Zap, Shield, QrCode, ClipboardList, Send, BarChart3, FileText, MessageSquare } from 'lucide-react';
import { useIsMobile } from '../../../hooks/use-mobile';

const LoginSlider = () => {
  const [slideIndex, setSlideIndex] = useState(0);
  const isMobile = useIsMobile();

  // Loop between CRM and WhatsApp content every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setSlideIndex((prev) => (prev + 1) % 2);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', width: '100%', minHeight: '0' }}>
      {/* Looping Content */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', overflow: 'hidden', minHeight: '0' }}>
        <AnimatePresence mode="wait">
          {slideIndex === 0 ? (
            <motion.div
              key="crm"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.4 }}
              style={{ width: '100%' }}
            >
              <div style={{ width: '100%' }}>
                {/* Badge */}
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: 'rgba(59, 130, 246, 0.2)',
                  color: '#60a5fa',
                  padding: '6px 12px',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: '600',
                  marginBottom: '12px'
                }}>
                  <Users style={{ width: '14px', height: '14px' }} />
                  CRM Solution
                </div>

                {/* Title with colored part */}
                <h1 style={{
                  fontSize: isMobile ? '24px' : '32px',
                  fontWeight: '700',
                  color: 'white',
                  lineHeight: '1.3',
                  margin: '0 0 12px 0',
                  maxWidth: '100%'
                }}>
                  Manage Customer Relationships with{' '}
                  <span style={{ color: 'hsl(158, 48%, 41%)' }}>Confidence</span>
                </h1>

                {/* Description */}
                <p style={{
                  fontSize: isMobile ? '14px' : '16px',
                  color: 'rgba(255,255,255,0.65)',
                  lineHeight: '1.6',
                  margin: '8px 0 0 0',
                  maxWidth: '100%'
                }}>
                  Track complaints, manage warranties, and provide excellent service with our industry-leading CRM.
                </p>

                {/* Features Grid */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? 'repeat(1, minmax(0, 1fr))' : 'repeat(3, 1fr)',
                  gap: '12px',
                  marginTop: '20px'
                }}>
                  <div style={{
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '12px',
                    padding: '16px',
                    textAlign: 'center'
                  }}>
                    <div style={{
                      height: '40px',
                      width: '40px',
                      background: 'rgba(255,255,255,0.1)',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 12px auto'
                    }}>
                      <Shield style={{ width: '20px', height: '20px', color: 'rgba(255,255,255,0.8)' }} />
                    </div>
                    <p style={{
                      color: 'rgba(255,255,255,0.9)',
                      fontWeight: '500',
                      fontSize: '13px',
                      margin: '0'
                    }}>
                      Warranty Management
                    </p>
                  </div>

                  <div style={{
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '12px',
                    padding: '16px',
                    textAlign: 'center'
                  }}>
                    <div style={{
                      height: '40px',
                      width: '40px',
                      background: 'rgba(255,255,255,0.1)',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 12px auto'
                    }}>
                      <QrCode style={{ width: '20px', height: '20px', color: 'rgba(255,255,255,0.8)' }} />
                    </div>
                    <p style={{
                      color: 'rgba(255,255,255,0.9)',
                      fontWeight: '500',
                      fontSize: '13px',
                      margin: '0'
                    }}>
                      Product Tracking
                    </p>
                  </div>

                  <div style={{
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '12px',
                    padding: '16px',
                    textAlign: 'center'
                  }}>
                    <div style={{
                      height: '40px',
                      width: '40px',
                      background: 'rgba(255,255,255,0.1)',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 12px auto'
                    }}>
                      <ClipboardList style={{ width: '20px', height: '20px', color: 'rgba(255,255,255,0.8)' }} />
                    </div>
                    <p style={{
                      color: 'rgba(255,255,255,0.9)',
                      fontWeight: '500',
                      fontSize: '13px',
                      margin: '0'
                    }}>
                      Complaint Resolution
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="whatsapp"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.4 }}
              style={{ width: '100%' }}
            >
              <div style={{ width: '100%' }}>
                {/* Badge */}
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: 'rgba(16, 185, 129, 0.2)',
                  color: '#10b981',
                  padding: '6px 12px',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: '600',
                  marginBottom: '12px'
                }}>
                  <Zap style={{ width: '14px', height: '14px' }} />
                  NEW: WhatsApp Business API
                </div>

                {/* Title with colored part */}
                <h1 style={{
                  fontSize: isMobile ? '24px' : '32px',
                  fontWeight: '700',
                  color: 'white',
                  lineHeight: '1.3',
                  margin: '0 0 12px 0',
                  maxWidth: '100%'
                }}>
                  Grow your business with{' '}
                  <span style={{ color: 'hsl(158, 48%, 41%)' }}>WhatsApp Automation</span>
                </h1>

                {/* Description */}
                <p style={{
                  fontSize: isMobile ? '14px' : '16px',
                  color: 'rgba(255,255,255,0.65)',
                  lineHeight: '1.6',
                  margin: '8px 0 0 0',
                  maxWidth: '100%'
                }}>
                  Send campaigns, manage templates, and engage customers directly from your CRM.
                </p>

                {/* Features Grid */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? 'repeat(1, minmax(0, 1fr))' : 'repeat(2, 1fr)',
                  gap: '12px',
                  marginTop: '20px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px',
                    borderRadius: '8px',
                    background: 'rgba(16, 185, 129, 0.1)',
                    border: '1px solid rgba(16, 185, 129, 0.2)'
                  }}>
                    <Send style={{ width: '16px', height: '16px', color: 'hsl(158, 48%, 41%)' }} />
                    <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px' }}>Bulk Messaging</span>
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px',
                    borderRadius: '8px',
                    background: 'rgba(16, 185, 129, 0.1)',
                    border: '1px solid rgba(16, 185, 129, 0.2)'
                  }}>
                    <BarChart3 style={{ width: '16px', height: '16px', color: 'hsl(158, 48%, 41%)' }} />
                    <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px' }}>Campaign Analytics</span>
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px',
                    borderRadius: '8px',
                    background: 'rgba(16, 185, 129, 0.1)',
                    border: '1px solid rgba(16, 185, 129, 0.2)'
                  }}>
                    <FileText style={{ width: '16px', height: '16px', color: 'hsl(158, 48%, 41%)' }} />
                    <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px' }}>Template Management</span>
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px',
                    borderRadius: '8px',
                    background: 'rgba(16, 185, 129, 0.1)',
                    border: '1px solid rgba(16, 185, 129, 0.2)'
                  }}>
                    <MessageSquare style={{ width: '16px', height: '16px', color: 'hsl(158, 48%, 41%)' }} />
                    <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px' }}>Conversations</span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Slide Indicators */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: '24px',
        padding: isMobile ? '12px 4px 8px' : '16px 20px 24px',
        marginBottom: isMobile ? '0' : '16px',
        borderRadius: '12px'
      }}>
        <p style={{
          color: 'rgba(255,255,255,0.5)',
          fontSize: '12px',
          margin: '0'
        }}>
          © 2026 Prodyt. All rights reserved.
        </p>
        <div style={{ display: 'flex', gap: '8px' }}>
          {[0, 1].map((i) => (
            <button
              key={i}
              onClick={() => setSlideIndex(i)}
              style={{
                height: '8px',
                borderRadius: '4px',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s',
                width: slideIndex === i ? '24px' : '8px',
                background: slideIndex === i ? 'hsl(158, 48%, 41%)' : 'rgba(255,255,255,0.3)',
                padding: '0'
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default LoginSlider;
